/* eslint-disable @typescript-eslint/no-explicit-any */

// Stolen from https://github.com/gparlakov/forms-typed/blob/21e99c91877746b506dd64ad0e5a127eeed15bac/projects/forms/src/lib/forms-typed.ts#L29-L36
import { AbstractControl, FormArray, FormControl, FormGroup, UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Observable } from 'rxjs';

// eslint-disable-next-line no-restricted-syntax -- NonGroup is the negative half of the group/control split: any callable counts, whatever it returns
export type NonGroup = string | number | boolean | ((...args: unknown[]) => unknown) | null | undefined | never;
/**
 * This is a strongly typed thin wrapper type around `FormGroup`.
 * Can be created using the `typedFormGroup` function
 */
export interface TypedFormGroup<K, C extends Controls<K> = TypedControlsIn<K>> extends UntypedFormGroup, TypedFormControl<K> {
  controls: K extends NonGroup ? never : C;
  valueChanges: Observable<K>;
  statusChanges: Observable<'VALID' | 'INVALID' | 'PENDING' | 'DISABLED'>;
  patchValue: (v: Partial<K>, options?: FormEventOptions) => void;
  setValue: (v: Partial<K>, options?: FormEventOptions) => void;
  value: K;
  setControl: <T extends keyof C>(name: T extends string ? T : never, control: C[T]) => void;
  reset: (value?: ResetValue<K>, options?: FormEventOptions) => void;
}

export type TypedControlsIn<K, groups extends keyof K = never, arrays extends keyof K = never> = {
  [key in keyof K]: key extends groups
    ? TypedFormGroup<K[key]>
    : key extends arrays
      ? K[key] extends Array<infer T>
        ? TypedFormArray<T[], T>
        : never
      : TypedFormControl<K[key]>;
};

interface FormEventOptions {
  emitEvent?: boolean;
  onlySelf?: boolean;
}

type ResetValue<K> = Partial<{
  [key in keyof K]: K[key] | { value: K[key]; disabled: boolean };
}>;

interface TypedFormControl<K> extends UntypedFormControl, AbstractControl {
  valueChanges: Observable<K>;
  statusChanges: Observable<'VALID' | 'INVALID' | 'PENDING' | 'DISABLED'>;
  patchValue: (v: Partial<K>, options?: FormEventOptions) => void;
  setValue: (v: K, options?: FormEventOptions) => void;
  value: K;
  reset: (value?: ResetValue<K>, opts?: FormEventOptions) => void;
}

export interface TypedFormArray<K extends Array<T> = any[], T = any> extends UntypedFormArray {
  valueChanges: Observable<K>;
  statusChanges: Observable<'VALID' | 'INVALID' | 'PENDING' | 'DISABLED'>;
  patchValue: (v: K, options?: FormEventOptions) => void;
  setValue: (v: K, options?: FormEventOptions) => void;
  controls: Array<TypedFormControl<T>>;
  push: (control: TypedFormControl<T>) => void;
  insert: (index: number, control: TypedFormControl<T>) => void;
  at: (index: number) => TypedFormControl<T>;
  setControl: (index: number, control: TypedFormControl<T>) => void;
  value: K;
}

export type Controls<K> =
  | TypedControlsIn<K>
  | {
      [k in keyof K]: K[k] extends Array<infer T>
        ? TypedFormControl<K[k]> | TypedFormGroup<K[k]> | TypedFormArray<K[k], T>
        : TypedFormControl<K[k]> | TypedFormGroup<K[k]>;
    };

// Not sure if these will be used in the future, so leaving for the moment

// interface TypedAbstractControl<K> extends FormControl, AbstractControl {
//   valueChanges: Observable<K>;
//   statusChanges: Observable<'VALID' | 'INVALID' | 'PENDING' | 'DISABLED'>;
//   patchValue: (v: Partial<K>, options?: FormEventOptions) => void;
//   setValue: (v: K, options?: FormEventOptions) => void;
//   value: K;
//   reset: (value?: ResetValue<K>, opts?: FormEventOptions) => void;
// }
/**
 * Shorthand for a model with `TypedFormControl`s and `TypedFormArray`s only (i.e. no `TypedFormGroup`s in)
 */
// type TypedArraysIn<K, arrays extends keyof K> = TypedControlsIn<K, never, arrays>;
/**
 * Shorthand for a model with `TypedFormControl`s and `TypedFormGroup`s only  (i.e. no `TypedFormArray`s in)
 */
// type TypedGroupsIn<K, groups extends keyof K> = TypedControlsIn<K, groups, never>;

/**
 * Get the interface without AbstractControl wrappers
 */
export type FormGroupValue<T extends FormGroup> = ReturnType<T['getRawValue']>;

/**
 * Get typed form group controls
 */
export type FormGroupControls<T extends FormGroup> = T['controls'];

// typings taken from https://github.com/browsepedia/ng-forms/blob/main/projects/ng-forms/src/lib/ng-forms.models.ts

/**
 * Given an type T, it generates the FormGroup T argument based on the values of each keys
 * mapping each value in the object to the corresponding AbstractControl implementation,
 * with the exception of primitive value arrays, which are mapped to FormControl.
 * Primitive values are mapped to FormControl
 * Object Arrays are mapped to FormArray.
 * Primitive type arrays, including Date and null (number[], Date[], etc) are mapped to FormControl
 * Objects are mapped to FormGroup.
 */
export type FlatFormControlsForm<T> = {
  [K in keyof T]-?: T[K] extends (infer ArrayElementType)[]
    ? ToFlatStrictFormArray<T[K], ArrayElementType>
    : T[K] extends Date
      ? FormControl<Date>
      : T[K] extends null
        ? FormControl<T[K]>
        : T[K] extends Record<any, any> | undefined
          ? FormGroup<FlatFormControlsForm<T[K]>>
          : FormControl<T[K]>;
};

type ToFlatStrictFormArray<Type, ArrayElementType> = ArrayElementType extends Date
  ? FormControl<Date>
  : ArrayElementType extends null
    ? FormControl<Type>
    : ArrayElementType extends Record<any, any> | undefined
      ? FlatFormArray<ArrayElementType>
      : FormControl<Type>;

export type FlatFormArray<T> = FormArray<FlatFormGroup<T>>;

export class FlatFormGroup<T> extends FormGroup<FlatFormControlsForm<T>> {}
